// --------------------------------------------------------------------------
// Shared fetch wrapper for all API calls
// Auto-adds Supabase auth token, unwraps { success, data } envelope
// --------------------------------------------------------------------------

import { supabase } from "./supabase";

/**
 * In production, call the Railway API directly (not through Vercel rewrite)
 * to ensure auth headers and SSE streams work correctly.
 * In dev, use relative paths (Vite proxy handles it).
 */
const viteEnv = import.meta.env ?? {};
export const API_BASE = viteEnv.VITE_API_URL || "";

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown,
  ) {
    super(`API ${status}: ${statusText}`);
    this.name = "ApiError";
  }
}

interface FetchOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

/**
 * Get the current auth token.
 *
 * Tries getSession() first (fast, reads localStorage).
 * If that returns null — which can happen in the brief window right after
 * login before Supabase has written the session — falls back to refreshSession()
 * which forces a round-trip to Supabase and returns a fresh token.
 */
type SupabaseAuthTokenClient = {
  getSession: () => Promise<{ data: { session: { access_token?: string | null } | null } }>;
  refreshSession: () => Promise<{ data: { session: { access_token?: string | null } | null } }>;
};

export async function getAuthToken(
  authClient: SupabaseAuthTokenClient | null = supabase?.auth ?? null,
): Promise<string | null> {
  if (!authClient) return null;

  const { data: sessionData } = await authClient.getSession();
  if (sessionData.session?.access_token) {
    return sessionData.session.access_token;
  }

  // Fallback: force a refresh to handle the post-login hydration race
  try {
    const { data: refreshData } = await authClient.refreshSession();
    return refreshData.session?.access_token ?? null;
  } catch {
    return null;
  }
}

/**
 * Core fetch wrapper.
 * - Adds Authorization header from Supabase session
 * - Unwraps the standard { success, data } envelope
 * - Logs 401s but does NOT auto-redirect (AuthProvider handles session expiry)
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: FetchOptions = {},
): Promise<T> {
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Don't set Content-Type for FormData (browser sets boundary automatically)
  if (options.body && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    headers,
    body:
      options.body instanceof FormData
        ? (options.body as BodyInit)
        : options.body
          ? JSON.stringify(options.body)
          : undefined,
  });

  if (res.status === 401 || res.status === 403) {
    console.warn(`[API] ${res.status} on ${url} — auth may have expired`);
    throw new ApiError(res.status, res.statusText);
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      /* empty */
    }
    throw new ApiError(res.status, res.statusText, body);
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  const json = await res.json();

  // Unwrap standard envelope
  if (json && typeof json === "object" && "success" in json && "data" in json) {
    return json.data as T;
  }

  return json as T;
}

/** Upload helper for FormData (documents) */
export async function apiUpload<T = unknown>(
  path: string,
  formData: FormData,
): Promise<T> {
  return apiFetch<T>(path, {
    method: "POST",
    body: formData,
  });
}
